/**
 * Creates the Bullhorn job submission the job detail screen's primary action produces [KPW-1].
 *
 * **This is the write that turns an application into a record.** Everything before it is the candidate
 * looking around; this is the moment ISS's system of record learns they applied, and an ISS automation
 * keyed on the submission's status starts the welcome-email → application → onboarding chain.
 *
 * The kiosk cannot see whether that automation fired, so this module deliberately reports only what it can
 * actually know: the api answered, or it did not.
 */

import { kioskFetch } from './kioskFetch';
export type SubmissionOutcome =
  | { readonly kind: 'created'; readonly submissionId: number; readonly status: string }
  /**
   * No submission exists.
   *
   * One value for every failure on purpose — writes disabled, Bullhorn refused, a 200 whose `messages[]`
   * reported an error, a dead socket. They are different lines in a log and the same fact to the person
   * standing at the screen, and KPW-1 forbids retrying with a different status, so there is nothing for a
   * caller to branch on. It routes to a representative.
   */
  | { readonly kind: 'unreachable' };

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

export async function createSubmission(
  candidateId: number,
  jobOrderId: number,
): Promise<SubmissionOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidateId, jobOrderId }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  if (!response.ok) return { kind: 'unreachable' };

  const body: unknown = await response.json().catch(() => null);
  // A generated type is a claim about the contract, not a runtime guarantee — `src/services/CLAUDE.md`.
  // Without an id there is nothing to show a recruiter, which is the same fact as no submission.
  if (typeof body !== 'object' || body === null) return { kind: 'unreachable' };
  const { submissionId, status } = body as { submissionId?: unknown; status?: unknown };
  if (typeof submissionId !== 'number') return { kind: 'unreachable' };

  return {
    kind: 'created',
    submissionId,
    // Reported by the api rather than assumed here: while OQ-61 is open the value in force is server
    // configuration, and "which status did that demo write" has to be answerable from the client too.
    status: typeof status === 'string' ? status : '',
  };
}
